import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { css, LitElement, html } from 'lit-element';
import AabIcon from '@aab/sc-aab-icon/dist/AabIcon';
import { sy_others_info, sy_others_info_solid, sy_shapes_close_large } from '@aab/sc-aab-icon-set/src/lib/systems';
import { createPopper } from '@popperjs/core';

var css_248z = css`/* Spacing */
.p-2 {
  padding: 16px !important;
}

.pb-0 {
  padding-bottom: 0px !important;
}

/* Utilities */
.d-flex {
  display: flex !important;
}

.d-none {
  display: none !important;
}

.flex-column {
  -ms-flex-direction: column !important;
  flex-direction: column !important;
}

.rounded-4 {
  border-radius: 4px !important;
}

/* Grid */
*,
*::before,
*::after {
  box-sizing: border-box;
}

.h4 h4,
.h4 .h4,
.h4 ::slotted(h4) {
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  line-height: 1.5rem;
  color: #222;
}
@media only screen and (max-width: 576px) {
  .h4 h4,
.h4 .h4,
.h4 ::slotted(h4) {
    font-size: 1.125rem;
    line-height: 1.5rem;
  }
}

.p p,
.p ::slotted(p) {
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
  margin-top: 0;
  margin-bottom: 1rem;
  line-height: 1.5;
  color: #222;
}

:host {
  display: inline-flex;
  vertical-align: middle;
  font-size: initial;
  font-weight: initial;
}

#info-button {
  display: inline-flex;
  cursor: pointer;
}

#popover {
  position: absolute;
  background-color: #fff;
  z-index: 400;
  width: 20rem;
  filter: drop-shadow(0px 0px 6px rgba(34, 34, 34, 0.02)) drop-shadow(0px 4px 8px rgba(34, 34, 34, 0.15));
  white-space: normal;
}

#close-button {
  float: right;
  cursor: pointer;
}

#arrow,
#arrow::before {
  position: absolute;
  width: 14px;
  height: 14px;
  z-index: -1;
}

#arrow::before {
  content: "";
  transform: rotate(45deg);
  background-color: #fff;
}

#popover[data-popper-placement^=top] > #arrow {
  bottom: -4px;
}

#popover[data-popper-placement^=bottom] > #arrow {
  top: -4px;
}

#popover[data-popper-placement^=left] > #arrow {
  right: -4px;
}

#popover[data-popper-placement^=right] > #arrow {
  left: -4px;
}`;

class AabInfoPopover extends ScopedElementsMixin(LitElement) {
  static get properties() {
    return {
      icon: {
        attribute: false,
        type: String,
      },
      open: {
        attribute: false,
        type: Boolean,
      },
      popper: {
        attribute: false,
        type: Object,
      },
    };
  }

  static get styles() {
    return [css_248z];
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
    };
  }

  constructor() {
    super();

    this.icon = sy_others_info;
    this.open = false;
    this.popper = null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', (event) => this.handleOutsideEvent(event));
    document.addEventListener('keydown', (event) => this.handleOutsideEvent(event));
  }

  disconnectedCallback() {
    document.removeEventListener('click', (event) => this.handleOutsideEvent(event));
    document.removeEventListener('keydown', (event) => this.handleOutsideEvent(event));
    super.disconnectedCallback();
  }

  updated() {
    if (this.open && this.popper) {
      this.popper.update();
    }
  }

  createPopper() {
    const popover = this.shadowRoot.querySelector('#popover');

    this.popper = createPopper(this, popover, {
      placement: 'top',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    });
  }

  handleIconHover(event) {
    if (this.open) {
      this.icon = sy_others_info_solid;
    } else if (!this.open && (event.type === 'mouseenter' || event.type === 'focusin')) {
      this.icon = sy_others_info_solid;
    } else {
      this.icon = sy_others_info;
    }
  }

  togglePopover() {
    this.open = !this.open;

    if (this.open) {
      if (!this.popper) {
        this.createPopper();
      }
    } else {
      this.destroyPopper();
    }
  }

  destroyPopper() {
    this.popper.destroy();
    this.popper = null;
    this.icon = sy_others_info;
  }

  handleOutsideEvent(event) {
    if (event.key === 'Escape' && this.open) {
      this.togglePopover();
      this.shadowRoot.querySelector('#info-button').focus();
      this.icon = sy_others_info_solid;
    } else if (this.open && !event.composedPath().filter((element) => element === this).length) {
      this.togglePopover();
    }
  }

  handleKeyDown(event) {
    if (
      event.key === 'Enter'
      || event.key === ' '
      || event.key === 'Spacebar'
    ) {
      event.preventDefault();
      this.togglePopover();
      this.shadowRoot.querySelector('#info-button').focus();
      this.icon = sy_others_info_solid;
    }
  }

  render() {
    return html`
      <aab-icon
        tabindex="0"
        size="2.5"
        role="button"
        aria-label="Open info-popover"
        aria-haspopup="true"
        id="info-button"
        svg="${this.icon}"
        @mouseenter="${this.handleIconHover}"
        @mouseleave="${this.handleIconHover}"
        @focusin="${this.handleIconHover}"
        @focusout="${this.handleIconHover}"
        @click="${this.togglePopover}"
        @keydown="${(event) => this.handleKeyDown(event)}"
      ></aab-icon>
      <div id="popover" role="dialog" aria-label="popover " class="${this.open ? 'd-flex' : 'd-none'} p-2 pb-0 flex-column border_box rounded-4">
        <div id="arrow" data-popper-arrow></div>
        <div class="h4 p">
          <aab-icon
            role="button"
            aria-label="Sluit info-popover"
            tabindex="0"
            id="close-button"
            size="3"
            svg="${sy_shapes_close_large}"
            @click="${this.togglePopover}"
            @keydown="${(event) => this.handleKeyDown(event)}"
          ></aab-icon>
          <div tabindex="0">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

export { AabInfoPopover as default };
